import { useState, useEffect } from "react";
import { Button } from "../Button";
import { TextField } from "../form-fields/TextField";
import { toast } from "react-toastify";
import { APIs } from "../../services/Apis";
import { logout } from "../../services/Utils";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";

export default function UserDetailsTab({ initialData = {} }) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [originalData, setOriginalData] = useState({
    displayName: "",
    email: "",
  });
  
  // Password reset state
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  useEffect(() => {
    if (initialData.name || initialData.email) {
      setDisplayName(initialData.name || "");
      setEmail(initialData.email || "");
      setOriginalData({
        displayName: initialData.name || "",
        email: initialData.email || "",
      });
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [initialData]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updates = [];

      if (displayName !== originalData.displayName) {
        updates.push("display name");
      }

      if (email !== originalData.email) {
        updates.push("email");
      }

      if (updates.length > 0) {
        toast.success(`Successfully updated ${updates.join(" and ")}`);
        setOriginalData({ displayName, email });
      } else {
        toast.info("No changes to save");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestCode = async () => {
    if (isResettingPassword) return;

    if (!email) {
      toast.error("Email address is required to reset password.");
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await APIs.resetPass(email);
      if (response.success) {
        toast.success("Verification code sent! Check your inbox.");
        setCodeSent(true);
      } else {
        toast.error(response.message || "Failed to send verification code. Please try again.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (isResettingPassword) return;

    if (!code) {
      toast.error("Please enter the verification code.");
      return;
    }

    if (!newPassword) {
      toast.error("Please enter a new password.");
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await APIs.resetPass(email, code, newPassword);
      if (response.success) {
        toast.success("Password reset successful!");
        setCodeSent(false);
        setCode("");
        setNewPassword("");
      } else {
        toast.error(response.message || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleBackToRequestCode = () => {
    setCodeSent(false);
    setCode("");
    setNewPassword("");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-600">Loading user details...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="">
        <div className="py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            User Information
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Update your personal information and account details
          </p>
        </div>
        <form onSubmit={handleSave} className="py-5 space-y-6">
          <TextField
            label="Display Name"
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your display name"
          />

          <TextField
            label="Email Address"
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              type="submit"
              variant="solid"
              color="primary"
              loading={isSaving}
              disabled={isSaving}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>

      {/* Password Reset Section */}
      <div className="mt-8">
        <div className="py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Change Password
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Reset your password using a verification code sent to your email
          </p>
        </div>
        {!codeSent ? (
          <div className="py-5">
            <p className="text-sm text-gray-600 mb-4">
              A verification code will be sent to <strong>{email || "your email address"}</strong>
            </p>
            <Button
              type="button"
              variant="solid"
              color="primary"
              onClick={handleRequestCode}
              loading={isResettingPassword}
              disabled={isResettingPassword || !email}
            >
              Send Verification Code
            </Button>
          </div>
        ) : (
          <form onSubmit={handleResetPassword} className="py-5 space-y-6">
            <TextField
              label="Verification Code"
              type="text"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter verification code"
              required
            />

            <TextField
              label="New Password"
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="text"
                color="slate"
                onClick={handleBackToRequestCode}
                disabled={isResettingPassword}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="solid"
                color="primary"
                loading={isResettingPassword}
                disabled={isResettingPassword}
              >
                Reset Password
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Logout Section */}
      <div className="mt-8">
        <div className="py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Logout</h2>
          <p className="mt-1 text-sm text-gray-500">
            Sign out of your account on this device
          </p>
        </div>
        <div className="py-5">
          <Button
            type="button"
            variant="outline"
            color="error"
            onClick={() => setShowLogoutDialog(true)}
          >
            Log Out
          </Button>
        </div>
      </div>

      <ConfirmationDialog
        open={showLogoutDialog}
        setOpen={setShowLogoutDialog}
        title="Log out"
        description="Are you sure you want to log out? You will need to sign in again to access your account."
        confirmationButton={
          <Button
            type="button"
            variant="solid"
            color="error"
            className="sm:ml-3"
            onClick={logout}
          >
            Log Out
          </Button>
        }
        cancelButton={
          <Button
            type="button"
            variant="outline"
            color="slate"
            onClick={() => setShowLogoutDialog(false)}
          >
            Cancel
          </Button>
        }
      />
    </div>
  );
}
